const JWT_KEY = "jwt_token";

// Companion keys of a stored session. The shared cookie carries the JWT only,
// so these are dropped rather than paired with another account's token.
// `navChoice` (the remembered organization / donor dashboard) stays: dropping
// it would put "Choose a Dashboard" in front of everyone coming over from the
// new dashboard, every time.
const SESSION_KEYS = ["c_t", "type", "orgId"];

// Any *.charidy.com page can set this cookie, so only a well-formed JWT is taken:
// three base64url segments, nothing that would need decoding.
const JWT_SHAPE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

// The first non-empty, well-formed `jwt_token` cookie, read as is.
function readSharedJwt() {
  for (const part of document.cookie.split(";")) {
    const entry = part.trim();
    if (!entry.startsWith(`${JWT_KEY}=`)) continue;
    const value = entry.slice(JWT_KEY.length + 1);
    if (JWT_SHAPE.test(value)) return value;
  }
  return null;
}

export function decodeJwtPayload(token) {
  try {
    const segment = token.split(".")[1];
    if (!segment) return null;
    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return payload && typeof payload === "object" && !Array.isArray(payload) ? payload : null;
  } catch {
    return null;
  }
}

// Expiry in Unix seconds, or NaN when the token carries none.
function expiresAt(payload) {
  const raw = payload.exp ?? payload.expiration_unix_timestamp_sec;
  if (typeof raw === "number") return raw;
  if (typeof raw === "string" && raw.trim() !== "") return Number(raw);
  return NaN;
}

// Same rule as the new dashboard (modules/auth/runtime/composables/use-jwt.ts):
// expired only when an expiry claim says so.
function isExpired(payload) {
  const expiration = expiresAt(payload);
  return Number.isFinite(expiration) && expiration <= Date.now() / 1000;
}

function hasOwnSession(store) {
  const jwt = localStorage.getItem(JWT_KEY);
  if (!store.state.user.user || !jwt) return false;

  const payload = decodeJwtPayload(jwt);
  return !payload || !isExpired(payload);
}

// Same login (`id` claim), issued later (`iat`) and lasting at least as long.
// The last condition keeps a short-lived token — e.g. from a 15-minute sign-in
// link in a team email — from replacing a full session.
function isNewerTokenOfSameLogin(shared, own) {
  if (!own || shared.id == null || own.id == null) return false;
  if (typeof shared.iat !== "number" || typeof own.iat !== "number") return false;
  return (
    String(shared.id) === String(own.id) &&
    shared.iat > own.iat &&
    expiresAt(shared) >= expiresAt(own)
  );
}

/**
 * Take over the session another Charidy app (the new dashboard, the public
 * site, a donation form) left in the shared `.charidy.com` `jwt_token` cookie
 * when this dashboard has none of its own — no signed-in user, or no or an
 * expired stored JWT. That is what lets a person move over from the new
 * dashboard without signing in again.
 *
 * A live own session always wins: a sign-in elsewhere never switches the
 * account under someone already signed in here. The one exception is a newer
 * token of the same login — the person signed out and back in elsewhere, which
 * also killed the token stored here.
 *
 * Runs on boot, before the axios Authorization header is set and before the
 * router's first navigation, and again when a request is refused with a bare
 * 401/403 (the caller reloads).
 *
 * Returns true when the shared session was adopted.
 */
export function adoptSharedSession(store) {
  const sharedJwt = readSharedJwt();
  const payload = sharedJwt ? decodeJwtPayload(sharedJwt) : null;
  if (!payload || isExpired(payload)) return false;

  if (hasOwnSession(store)) {
    const own = decodeJwtPayload(localStorage.getItem(JWT_KEY));
    if (!isNewerTokenOfSameLogin(payload, own)) return false;

    // Same person: keep the org selection and the nav choice, swap the token.
    // `c_t` came with the old token; an empty `token` keeps `setUser` from
    // rewriting storage and the cookie.
    localStorage.setItem(JWT_KEY, sharedJwt);
    localStorage.removeItem("c_t");
    const user = store.state.user.user;
    store.commit("setUser", {
      ...user,
      attributes: { ...user.attributes, jwt_token: sharedJwt, token: "" },
    });
    return true;
  }

  localStorage.setItem(JWT_KEY, sharedJwt);
  SESSION_KEYS.forEach((key) => localStorage.removeItem(key));

  // The shape a login leaves in the store, as far as the cookie can tell:
  // the account id is the JWT's `id` claim. `token` (c_t) is not in the
  // cookie; leaving it empty also keeps `setUser` from rewriting storage and
  // the cookie.
  store.commit("setUser", {
    id: String(payload.id ?? ""),
    type: "",
    attributes: { jwt_token: sharedJwt, token: "" },
  });

  return true;
}
